import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const useProgress = () => {
  const { t } = useTranslation();

  // ✅ 단순한 진행률 모달 (열기/닫기만)
  const [progressModal, setProgressModal] = useState({
    open: false,
    currentMessage: '',
    progress: 0,
    message: ''
  });

  // ✅ 시각적 진행률 (부드러운 애니메이션용)
  const [visualProgress, setVisualProgress] = useState(0);
  const [targetProgress, setTargetProgress] = useState(0);
  const progressIntervalRef = useRef(null);

  // ✅ 광역 복사용 진행률 모달 상태
  const [globalCopyModal, setGlobalCopyModal] = useState({
    open: false,
    videoTitle: ''
  });

  // 진행률에 따른 메시지 계산 함수
  const getMessageByPercentage = useCallback((percentage) => {
    if (percentage >= 0 && percentage <= 10) {
      return t('analyze.extractingSubtitles'); // "영상의 자막을 추출 중입니다."
    } else if (percentage > 10 && percentage < 30) {
      return t('analyze.translatingSubtitles'); // "추출된 자막을 번역 중입니다."
    } else if (percentage >= 30 && percentage < 60) {
      return t('analyze.summarizingContent'); // "영상의 자막을 요약 중입니다."
    } else if (percentage >= 60 && percentage < 70) {
      return t('analyze.organizingKey'); // "핵심 내용을 정리 중입니다."
    } else if (percentage >= 70 && percentage < 80) {
      return t('analyze.organizingChapters'); // "챕터별로 핵심 내용을 정리 중입니다."
    } else if (percentage >= 80 && percentage <= 100) {
      return t('analyze.generatingQuestions'); // "심층 이해를 위한 추천질문을 생성 중입니다."
    }
    return t('common.startingAnalysis');
  }, [t]);

  // ✅ 시각적 진행률 업데이트 useEffect
  useEffect(() => {
    if (!progressModal.open) {
      // 모달이 닫혀있으면 모든 진행률 리셋
      setVisualProgress(0);
      setTargetProgress(0);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      return;
    }

    // 타겟 진행률 설정
    setTargetProgress(progressModal.progress || 0);

    // 기존 인터벌 정리
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    // 시각적 진행률 업데이트 인터벌
    progressIntervalRef.current = setInterval(() => {
      setVisualProgress(current => {
        const target = targetProgress;
        
        // ✅ 목표값이 100%면 즉시 100%로 설정
        if (target === 100) {
          return 100;
        }
        
        // 목표값에 도달했으면 더 이상 증가하지 않음
        if (current >= target) {
          // 하지만 목표값이 100%가 아니라면 조금씩 증가
          if (target < 100) {
            // 80% 이상에서는 더 적극적인 시각적 증가 (최대 +15%까지)
            if (target >= 80) {
              const maxProgress = Math.min(target + 15, 99);
              if (current < maxProgress) {
                return current + 0.1; // 천천히 하지만 더 많이 증가
              }
            } else {
              // 80% 미만에서는 기존 로직 유지 (최대 +5%까지)
              const maxProgress = Math.min(target + 5, 100);
              if (current < maxProgress) {
                return current + 0.2; // 매우 천천히 증가
              }
            }
          }
          return current;
        }
        
        // 목표값으로 빠르게 수렴
        const diff = target - current;
        if (diff > 10) {
          return current + 2; // 큰 차이가 나면 빠르게
        } else if (diff > 1) {
          return current + 0.5; // 작은 차이가 나면 천천히
        } else {
          return target; // 거의 근접하면 목표값으로
        }
      });
    }, 100); // 100ms마다 업데이트

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [progressModal.open, targetProgress]);

  // ✅ 실제 진행률이 업데이트되면 타겟 진행률 변경
  useEffect(() => {
    if (progressModal.open && progressModal.progress !== undefined) {
      setTargetProgress(progressModal.progress);
    }
  }, [progressModal.progress, progressModal.open]);

  // 진행률 모달 열기 함수
  const openProgressModal = (initialMessage = '', initialProgress = 0) => {
    setProgressModal({
      open: true,
      currentMessage: initialMessage || getMessageByPercentage(initialProgress),
      progress: initialProgress,
      message: initialMessage || getMessageByPercentage(initialProgress)
    });
    
    // 백그라운드 재접속 시 초기 진행률도 시각적으로 반영
    if (initialProgress > 0) {
      setVisualProgress(initialProgress);
      setTargetProgress(initialProgress);
    }
  };

  // 진행률 업데이트 함수
  const updateProgress = (progress, message = '') => {
    setProgressModal(prev => ({
      ...prev,
      progress: progress,
      currentMessage: message || getMessageByPercentage(progress),
      message: message || getMessageByPercentage(progress)
    }));
  };

  // 진행률 모달 닫기 함수
  const closeProgressModal = () => {
    setProgressModal(prev => ({ ...prev, open: false }));
  };

  // 광역 복사 모달 관리
  const openGlobalCopyModal = (videoTitle) => {
    setGlobalCopyModal({
      open: true,
      videoTitle: videoTitle
    });
  };

  const closeGlobalCopyModal = () => {
    setGlobalCopyModal({
      open: false,
      videoTitle: ''
    });
  };

  return {
    // 진행률 모달 상태
    progressModal,
    setProgressModal,
    visualProgress,
    
    // 광역 복사 모달 상태
    globalCopyModal,
    setGlobalCopyModal,
    
    // 함수들
    getMessageByPercentage,
    openProgressModal,
    updateProgress,
    closeProgressModal,
    openGlobalCopyModal,
    closeGlobalCopyModal,
  };
}; 